import { useComboboxFilter } from "@fluentui/react-components";
import { useStaticVirtualizerMeasure, Virtualizer } from "@fluentui/react-components/unstable";

import { useMemo } from "react";
import { useAppData } from "../../../hooks";
import { useCategoryNameMap } from "../../../hooks/useCategoryNameMap";
import { useTagsOptionsListStyles } from "../styles/TagsOptionsList.styles";
import { Tag } from "../../../models/tag";

type TagsOptionsListProps = {
  tags: Tag[];
  query: string;
  showCategories?: boolean;
  itemHeight: number;
  appId: string;
};

export const TagsOptionsList = ({ tags, query, showCategories, itemHeight, appId }: TagsOptionsListProps) => {
  const styles = useTagsOptionsListStyles();

  const [workspaceId] = useAppData<string>(appId, "workspaceId");

  const categoryNameMap = useCategoryNameMap(workspaceId);

  const options = useMemo(
    () =>
      tags?.map((tag: any) => {
        const categoryName = categoryNameMap[tag.tagCategoryId] ?? "Unknown";
        return {
          children: showCategories ? `${tag.name} (${categoryName})` : tag.name,
          value: `${tag.tagId}`,
        };
      }) ?? [],
    [tags, categoryNameMap, showCategories]
  );
  const children = useComboboxFilter(query, options, {
    noOptionsMessage: "No results match your search.",
    optionToText: (option) => option.children,
  });
  const numberOfItems = children.length;
  const { virtualizerLength, bufferItems, bufferSize, scrollRef, containerSizeRef } = useStaticVirtualizerMeasure({
    defaultItemSize: itemHeight,
    direction: "vertical",
  });

  return (
    <div aria-label="Virtualizer Example" className={styles.container} role={"list"} ref={scrollRef}>
      <Virtualizer
        numItems={numberOfItems}
        virtualizerLength={virtualizerLength}
        bufferItems={bufferItems}
        bufferSize={bufferSize}
        itemSize={itemHeight}
        containerSizeRef={containerSizeRef}
      >
        {(index) => {
          return <div className={styles.option}>{children[index]}</div>;
        }}
      </Virtualizer>
    </div>
  );
};
